import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const TransferForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [formData, setFormData] = useState({
    player_id: '',
    club_id: '',
    status: 'interested',
    transfer_fee: '',
    transfer_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchPlayersAndClubs();
    if (isEdit) {
      fetchTransfer();
    }
  }, [id]);

  const fetchPlayersAndClubs = async () => {
    try {
      const [playersRes, clubsRes] = await Promise.all([
        api.get('/players'),
        api.get('/clubs')
      ]);
      setPlayers(playersRes.data);
      setClubs(clubsRes.data);
    } catch (error) {
      toast.error('Failed to load players and clubs');
    }
  };

  const fetchTransfer = async () => {
    try {
      const response = await api.get(`/transfers/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load transfer');
      navigate('/transfers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        transfer_fee: formData.transfer_fee ? parseFloat(formData.transfer_fee) : null
      };

      if (isEdit) {
        await api.put(`/transfers/${id}`, payload);
        toast.success('Transfer updated successfully');
      } else {
        await api.post('/transfers', payload);
        toast.success('Transfer created successfully');
      }
      navigate('/transfers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transfer');
    } finally {
      setLoading(false);
    }
  };

  const statuses = [
    { value: 'interested', label: 'Interested' },
    { value: 'negotiating', label: 'Negotiating' },
    { value: 'offer_made', label: 'Offer Made' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/transfers"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Transfers</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            {isEdit ? 'Edit Transfer' : 'Add New Transfer'}
          </h1>
          <p className="text-lg text-zinc-300">Enter transfer details below</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="glass-card p-8 space-y-6">
            {/* Basic Info */}
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Transfer Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Player *
                  </label>
                  <select
                    required
                    data-testid="transfer-player-select"
                    value={formData.player_id}
                    onChange={(e) => setFormData({ ...formData, player_id: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="">Select a player</option>
                    {players.map((player) => (
                      <option key={player.id} value={player.id}>
                        {player.name} - {player.position}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Club *
                  </label>
                  <select
                    required
                    data-testid="transfer-club-select"
                    value={formData.club_id}
                    onChange={(e) => setFormData({ ...formData, club_id: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="">Select a club</option>
                    {clubs.map((club) => (
                      <option key={club.id} value={club.id}>
                        {club.name} - {club.country}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Status *
                  </label>
                  <select
                    required
                    data-testid="transfer-status-select"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    {statuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Transfer Date
                  </label>
                  <input
                    type="text"
                    value={formData.transfer_date}
                    onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., January 2026"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Transfer Fee (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.transfer_fee}
                    onChange={(e) => setFormData({ ...formData, transfer_fee: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., 50000000"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Additional Notes</h2>
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                  Notes
                </label>
                <textarea
                  rows="5"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  placeholder="Any additional notes about the transfer negotiation, terms, or concerns..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-white/5 flex gap-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="save-transfer-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" strokeWidth={2} />
                {loading ? 'Saving...' : isEdit ? 'Update Transfer' : 'Create Transfer'}
              </button>
              <Link
                to="/transfers"
                className="px-6 py-3 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm transition-all duration-200 uppercase tracking-wide font-bold"
              >
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferForm;